import React, { useEffect, useState, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { Transaction } from '../../types';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillTransfer, faRotate, faUniversity,
  faArrowUp, faArrowDown, faCheckCircle, faHourglass,
  faReceipt, faBuilding, faFileInvoice, faWifi, faSearch,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { LiveViewToggle, LiveModeBufferBadge } from '../../components/shared/LiveViewToggle';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiButton,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';
import socketService from '../../services/socketService';
import bankLogos from 'thai-banks-logo/banks-logo.json';
import dayjs from 'dayjs';

const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const SEARCH_INPUT_CLASS =
  'w-full pl-9 pr-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

// map bank_code (from bankList.ts) to thai-banks-logo key
const bankCodeToLogoKey: Record<string, string> = {
  '002': 'BBL', '004': 'KBANK', '006': 'KTB', '011': 'TTB', '014': 'SCB',
  '017': 'CITI', '022': 'CIMB', '024': 'UOB', '025': 'BAY', '030': 'GSB',
  '031': 'HSBC', '033': 'GHB', '034': 'BAAC', '066': 'IBANK', '067': 'TISCO',
  '069': 'KKP', '070': 'ICBC', '071': 'TCRB', '073': 'LHB',
};

// map slip short names to thai-banks-logo key
const slipBankMap: Record<string, string> = {
  KBNK: 'KBANK', KBANK: 'KBANK', KTB: 'KTB', SCB: 'SCB', BBL: 'BBL',
  BAY: 'BAY', TMB: 'TTB', TTB: 'TTB', GSB: 'GSB', GSBA: 'GSB', GHB: 'GHB', BAAC: 'BAAC',
  CIMB: 'CIMB', CIMBT: 'CIMB', UOB: 'UOB', UOBT: 'UOB', TISCO: 'TISCO',
  KKP: 'KKP', KKB: 'KKP', LHB: 'LHB', ICBC: 'ICBC', TCRB: 'TCRB', HSBC: 'HSBC',
  CITI: 'CITI', IBANK: 'IBANK', ISBT: 'IBANK',
};

function getBankIconFromText(text: string) {
  const match = text.match(/- (\w+)/);
  if (!match) {
    // SCB ไม่แนบชื่อธนาคารมา — ถ้าเป็น "เงินโอนจาก ชื่อคน" โดยไม่มี "- BANK" ให้ถือว่าเป็น SCB
    if (/เงินโอนจาก\s+[A-Z]/.test(text)) return bankLogos['SCB'].icon;
    return null;
  }
  const code = match[1].toUpperCase();
  const logoKey = slipBankMap[code] as keyof typeof bankLogos | undefined;
  if (!logoKey || !bankLogos[logoKey]) return null;
  return bankLogos[logoKey].icon;
}

function getBankIconFromCode(bankCode: string) {
  const logoKey = bankCodeToLogoKey[bankCode] as keyof typeof bankLogos | undefined;
  if (!logoKey || !bankLogos[logoKey]) return null;
  return bankLogos[logoKey].icon;
}

const AllTransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [realtimeMode, setRealtimeMode] = useState(true);
  const [bufferedNewTx, setBufferedNewTx] = useState(0);
  const today = dayjs().format('YYYY-MM-DD');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [appliedDateFrom, setAppliedDateFrom] = useState(today);
  const [appliedDateTo, setAppliedDateTo] = useState(today);
  const [filter, setFilter] = useState({
    is_matched: '',
    bank_id: '',
    source: '',
  });
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimer = useRef<NodeJS.Timeout>(undefined);
  const [banks, setBanks] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 50,
    total: 0
  });
  const [summary, setSummary] = useState({
    total: 0,
    matched: 0,
    unmatched: 0,
    total_amount: 0,
    slip_count: 0,
    webhook_count: 0
  });

  useEffect(() => {
    loadBanks();
  }, []);

  useEffect(() => {
    loadTransactions();
  }, [filter, searchQuery, appliedDateFrom, appliedDateTo, pagination.currentPage, pagination.limit]);

  // 🔔 Socket.IO realtime - รับ transaction ใหม่
  useEffect(() => {
    const handleNewTransaction = (data: any) => {
      if (realtimeMode) {
        if (pagination.currentPage === 1) {
          setTransactions(prev => [data, ...prev].slice(0, pagination.limit));
          setSummary(prev => ({
            ...prev,
            total: prev.total + 1,
            unmatched: prev.unmatched + 1,
            total_amount: prev.total_amount + (data.amount || 0),
            webhook_count: data.source === 'slip' ? prev.webhook_count : prev.webhook_count + 1,
            slip_count: data.source === 'slip' ? prev.slip_count + 1 : prev.slip_count
          }));
          setPagination(prev => ({ ...prev, total: prev.total + 1 }));
        }
      } else {
        setBufferedNewTx(prev => prev + 1);
      }
    };

    const handleTransactionMatched = (data: any) => {
      if (realtimeMode) {
        setTransactions(prev =>
          prev.map(t =>
            t.id === data.id ? { ...t, is_matched: true, matched_order_id: data.matched_order_id } : t
          )
        );
        setSummary(prev => ({
          ...prev,
          matched: prev.matched + 1,
          unmatched: Math.max(0, prev.unmatched - 1)
        }));
      } else {
        setBufferedNewTx(prev => prev + 1);
      }
    };

    socketService.on('new-transaction', handleNewTransaction);
    socketService.on('transaction-matched', handleTransactionMatched);

    return () => {
      socketService.off('new-transaction', handleNewTransaction);
      socketService.off('transaction-matched', handleTransactionMatched);
    };
  }, [pagination.currentPage, pagination.limit, realtimeMode]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchQuery(value);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 500);
  };

  const loadBanks = async () => {
    try {
      const response = await merchantApi.get('/admin/banks?with_merchants=true');
      setBanks(response.data.data);
    } catch (error) {
      console.error('Error loading banks');
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const offset = (pagination.currentPage - 1) * pagination.limit;
      let url = `/admin/transactions?limit=${pagination.limit}&offset=${offset}`;
      
      if (filter.is_matched) url += `&is_matched=${filter.is_matched}`;
      if (filter.bank_id) url += `&bank_id=${filter.bank_id}`;
      if (filter.source) url += `&source=${filter.source}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (appliedDateFrom) url += `&start_date=${appliedDateFrom}`;
      if (appliedDateTo) url += `&end_date=${appliedDateTo}`;
      
      const response = await merchantApi.get(url);
      setTransactions(response.data.data);
      
      if (response.data.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total
        }));
      }
      if (response.data.summary) {
        setSummary(response.data.summary);
      }
    } catch (error: any) {
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilter({ ...filter, [key]: value });
    setPagination({ ...pagination, currentPage: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, currentPage: page });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination({ ...pagination, limit: pageSize, currentPage: 1 });
  };

  const toggleRealtimeMode = () => {
    if (!realtimeMode && bufferedNewTx > 0) {
      loadTransactions();
      setBufferedNewTx(0);
      toast.info(`ดึงข้อมูลใหม่ พร้อมอัพเดท ${bufferedNewTx} รายการล่าสุด`);
    }
    setRealtimeMode((prev) => !prev);
  };

  const handleFilterReset = () => {
    setDateFrom(today);
    setDateTo(today);
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
    setFilter({ is_matched: '', bank_id: '', source: '' });
    setSearchInput('');
    setSearchQuery('');
    setPagination({ currentPage: 1, limit: 50, total: 0 });
  };

  const handleFilterSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Stats จาก API (รวมทั้งหมดที่ filter ไม่ใช่แค่หน้าปัจจุบัน)
  const totalCount = summary.total;
  const matchedCount = summary.matched;
  const unmatchedCount = summary.unmatched;
  const totalAmount = summary.total_amount;

  // Define columns for DataTable
  const columns: Column<Transaction>[] = [
    {
      key: 'bank',
      header: 'ธนาคาร',
      render: (tx) => {
        const bank = (tx as any).bank || (tx as any).banks;
        const bankCode = bank?.bank_code || '';
        const icon = getBankIconFromCode(bankCode);
        return (
          <div className="flex items-center gap-3">
            {icon ? (
              <img src={icon} alt={bankCode} className="w-10 h-10 rounded-lg object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                <FontAwesomeIcon icon={faUniversity} />
              </div>
            )}
            <div>
              <p className="font-medium text-gray-800 dark:text-slate-100 text-sm">{bank?.name || '-'}</p>
              <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-xs font-medium">
                {bankCode || '-'}
              </span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'merchant',
      header: 'Merchant',
      render: (tx) => (
        (tx as any).merchant ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <FontAwesomeIcon icon={faBuilding} className="text-xs" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{(tx as any).merchant.name}</p>
              <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{(tx as any).merchant.email}</p>
            </div>
          </div>
        ) : (
          <span className="text-gray-300 dark:text-slate-600">-</span>
        )
      )
    },
    {
      key: 'transaction_type',
      header: 'ประเภท',
      sortable: true,
      render: (tx) => (
        tx.transaction_type === 'in' ? (
          <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faArrowUp} /> เงินเข้า
          </span>
        ) : (
          <span className="px-2.5 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faArrowDown} /> เงินออก
          </span>
        )
      )
    },
    {
      key: 'amount',
      header: 'จำนวนเงิน',
      sortable: true,
      render: (tx) => (
        <div>
          <p className={`font-bold text-sm ${tx.transaction_type === 'in' ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
            ฿{tx.amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
          </p>
          {tx.is_matched && (tx as any).order && (
            <p className="text-xs text-gray-400 dark:text-slate-500">Order: {(tx as any).order.order_number}</p>
          )}
        </div>
      )
    },
    {
      key: 'reference_id' as any,
      header: 'แหล่งที่มา',
      render: (tx) => {
        const isSlip = (tx as any).reference_id && (tx as any).reference_id.startsWith('SLIP-');
        return isSlip ? (
          <div>
            <span className="px-2.5 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-semibold inline-flex items-center gap-1">
              <FontAwesomeIcon icon={faFileInvoice} /> Manual Slip
            </span>
            <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">{(tx as any).raw_data?.approved_by || ''}</p>
          </div>
        ) : (
          <span className="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faWifi} /> Webhook
          </span>
        );
      }
    },
    {
      key: 'from_account',
      header: 'จาก / ถึง',
      render: (tx) => {
        const bank = (tx as any).bank || (tx as any).banks;
        const bankCode = bank?.bank_code || '';
        const bankIcon = getBankIconFromCode(bankCode);
        const fromIcon = tx.from_account ? (getBankIconFromText(tx.from_account) || bankIcon) : null;
        const toIcon = tx.to_account ? (getBankIconFromText(tx.to_account) || bankIcon) : null;
        return (
          <div className="text-xs text-gray-500 dark:text-slate-400 space-y-1">
            {tx.from_account ? (
              <>
                <div className="flex items-center gap-1.5">
                  {fromIcon && <img src={fromIcon} alt="" className="w-5 h-5 rounded-full object-contain" />}
                  <span>จาก: {tx.from_account}</span>
                </div>
                {tx.to_account && (
                  <div className="flex items-center gap-1.5">
                    {toIcon && <img src={toIcon} alt="" className="w-5 h-5 rounded-full object-contain" />}
                    <span>ถึง: {tx.to_account}</span>
                  </div>
                )}
              </>
            ) : '-'}
          </div>
        );
      }
    },
    {
      key: 'transaction_date',
      header: 'วัน-เวลาสลิป',
      sortable: true,
      render: (tx) => (
        <div>
          <p className="text-sm text-gray-700 dark:text-slate-200">
            {new Date(tx.transaction_date).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', timeZone: 'Asia/Bangkok' })}
          </p>
          <p className="text-xs text-gray-400 dark:text-slate-500">
            {new Date(tx.transaction_date).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' })}
          </p>
        </div>
      )
    },
    {
      key: 'created_at',
      header: 'วันที่สร้าง',
      sortable: true,
      render: (tx) => (
        <div>
          <p className="text-sm text-gray-700 dark:text-slate-200">
            {new Date(tx.created_at).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', timeZone: 'Asia/Bangkok' })}
          </p>
          <p className="text-xs text-gray-400 dark:text-slate-500">
            {new Date(tx.created_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' })}
          </p>
        </div>
      )
    },
    {
      key: 'is_matched',
      header: 'สถานะ',
      sortable: true,
      render: (tx) => (
        tx.is_matched ? (
          <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faCheckCircle} /> Matched
          </span>
        ) : (
          <span className="px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faHourglass} /> Unmatched
          </span>
        )
      )
    }
  ];

  const statCards: StatCardDef[] = [
    {
      icon: faReceipt,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-accent)' },
      label: 'Total',
      value: <span style={{ color: 'var(--theme-accent)' }}>{totalCount.toLocaleString()}</span>,
    },
    {
      icon: faCheckCircle,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'Matched',
      value: <span className="text-green-600 dark:text-green-400">{matchedCount.toLocaleString()}</span>,
    },
    {
      icon: faHourglass,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'Unmatched',
      value: <span className="text-amber-600 dark:text-amber-400">{unmatchedCount.toLocaleString()}</span>,
    },
    {
      icon: faWifi,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'Webhook',
      value: <span className="text-blue-600 dark:text-blue-400">{summary.webhook_count.toLocaleString()}</span>,
    },
    {
      icon: faFileInvoice,
      iconClassName: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      label: 'Manual Slip',
      value: <span className="text-purple-600 dark:text-purple-400">{summary.slip_count.toLocaleString()}</span>,
    },
    {
      icon: faMoneyBillTransfer,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'ยอดรวม',
      value: (
        <span className="text-base" style={{ color: 'var(--theme-primary)' }}>
          ฿{totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faMoneyBillTransfer}
        title="All Transactions"
        subtitle={`รายการธุรกรรมจากธนาคาร • ${pagination.total.toLocaleString()} รายการ`}
        below={<LiveViewToggle realtimeMode={realtimeMode} onToggle={toggleRealtimeMode} />}
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleFilterSearch}
        onReset={handleFilterReset}
        loading={loading}
        actions={
          <UiButton variant="secondary" icon={faRotate} onClick={() => loadTransactions()}>
            Refresh
          </UiButton>
        }
      >
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            ค้นหา
          </label>
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="ค้นหา Order, ชื่อ, จำนวนเงิน..."
              className={SEARCH_INPUT_CLASS}
            />
          </div>
        </div>

        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <select
            value={filter.is_matched}
            onChange={(e) => handleFilterChange('is_matched', e.target.value)}
            className={FILTER_SELECT_CLASS}
          >
            <option value="">ทั้งหมด</option>
            <option value="true">Matched</option>
            <option value="false">Unmatched</option>
          </select>
        </div>

        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">ธนาคาร</label>
          <select
            value={filter.bank_id}
            onChange={(e) => handleFilterChange('bank_id', e.target.value)}
            className={FILTER_SELECT_CLASS}
          >
            <option value="">ทุกธนาคาร</option>
            {banks.map((b) => (
              <option key={b.id} value={b.id}>{b.name} ({b.bank_code})</option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">แหล่งที่มา</label>
          <select
            value={filter.source}
            onChange={(e) => handleFilterChange('source', e.target.value)}
            className={FILTER_SELECT_CLASS}
          >
            <option value="">ทั้งหมด</option>
            <option value="webhook">Webhook</option>
            <option value="slip">Manual Slip</option>
          </select>
        </div>
      </UiFilterPanel>

      <div className="flex justify-end mb-2">
        <LiveModeBufferBadge
          count={bufferedNewTx}
          onFlush={() => { loadTransactions(); setBufferedNewTx(0); toast.success('อัพเดทข้อมูลแล้ว'); }}
        />
      </div>

      <UiStatsGrid cards={statCards} loading={loading} cols={3} />

      <DataTable
        data={transactions}
        columns={columns}
        loading={loading}
        emptyMessage="ไม่มีรายการธุรกรรม"
        emptyIcon={<FontAwesomeIcon icon={faMoneyBillTransfer} className="text-5xl text-gray-200 dark:text-slate-600" />}
        serverPagination={{
          currentPage: pagination.currentPage,
          totalItems: pagination.total,
          pageSize: pagination.limit,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
        }}
        hoverable
        compact
      />
    </div>
  );
};

export default AllTransactionsPage;
